"use client"

import { useState } from "react"
import { DialogShell } from "@/components/graphite/dialog-shell"
import { PageAction } from "@/components/graphite/page-header"
import { cn } from "@/lib/utils"
import { Building2, Check } from "lucide-react"

type OrganizationSelectorDialogProps = {
    /** Orgs the user belongs to, from `GET /orgObs/list`. */
    organizations: { id: string; name: string }[]
    onSelect: (organizationId: string) => void
}

export function OrganizationSelectorDialog({ organizations, onSelect }: OrganizationSelectorDialogProps) {
    const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null)

    return (
        <DialogShell
            open
            onOpenChange={() => {}}
            /* There is nothing behind this one: no organization means no data
               to show, so it has no X, no escape and no click-away. */
            dismissible={false}
            wide
            title="Select Your Organization"
            description="Choose the organization you want to work with"
            footer={
                <PageAction
                    tone="primary"
                    onClick={() => selectedOrgId && onSelect(selectedOrgId)}
                    disabled={!selectedOrgId}
                >
                    Continue
                </PageAction>
            }
        >
            {/* Rows on one surface rather than a stack of cards, and no member
                count or plan: `GET /orgObs/list` carries neither, and the
                "_ members • _ plan" they replaced was a literal underscore
                printed where a number should be. */}
            <div className="divide-y">
                {organizations.map((org) => {
                    const chosen = selectedOrgId === org.id
                    return (
                        <button
                            key={org.id}
                            type="button"
                            aria-pressed={chosen}
                            onClick={() => setSelectedOrgId(org.id)}
                            className="flex w-full items-center gap-3 py-3 text-left transition-colors hover:bg-foreground/[0.03]"
                        >
                            <span className="flex size-9 flex-none items-center justify-center rounded-md bg-primary/10">
                                <Building2 className="size-[18px] text-brand-ink" />
                            </span>
                            <span className="min-w-0 flex-1 truncate text-[13px] font-medium leading-[1.3]">
                                {org.name}
                            </span>
                            <span
                                className={cn(
                                    "flex size-5 flex-none items-center justify-center rounded-full border transition-colors",
                                    chosen
                                        ? "border-transparent bg-primary text-primary-foreground"
                                        : "text-transparent",
                                )}
                            >
                                <Check className="size-3" />
                            </span>
                        </button>
                    )
                })}
            </div>
        </DialogShell>
    )
}
